import { delay } from '../promise/utils';
import type { UnwrapPromise } from '../types';
import { durationToMs } from './duration';
import { errToLongString } from './format';

/**
 * Idea taken from https://github.com/ivanhofer/exceptionally
 */
export interface GoodResult<Good> {
	what: 'GoodResult<Good>';
	isGood: true;
	isBad: false;
	value: Good;
}

export interface BadResult<Bad> {
	what: 'BadResult<Bad>';
	isGood: false;
	isBad: true;
	error: Bad;
}

export type GoodBadResult<Good, Bad> = GoodResult<Good> | BadResult<Bad>;

export type GoodOf<T extends (...args: any[]) => any> = UnwrapPromise<
	ReturnType<T>
> extends GoodBadResult<infer G, any>
	? G
	: never;

export type BadOf<T extends (...args: any[]) => any> = UnwrapPromise<
	ReturnType<T>
> extends GoodBadResult<any, infer B>
	? B
	: never;

export type ShouldNotThrow<T> = T;

export function good(value?: undefined): GoodResult<void>;
export function good<T>(value: T): GoodResult<T>;
export function good<T>(value: T): GoodResult<T> {
	return {
		what: 'GoodResult<Good>',
		isGood: true,
		isBad: false,
		value: value,
	};
}

export function bad(value?: undefined): BadResult<void>;
export function bad<T>(value: T): BadResult<T>;
export function bad<T>(error: T): BadResult<T> {
	return {
		what: 'BadResult<Bad>',
		isGood: false,
		isBad: true,
		error: error,
	};
}

export function isGood<Good, Bad>(thing: GoodBadResult<Good, Bad>): thing is GoodResult<Good> {
	return thing.isGood;
}

export function isBad<Good, Bad>(thing: GoodBadResult<Good, Bad>): thing is BadResult<Bad> {
	return thing.isBad;
}

export function guardGood<T extends GoodResult<unknown>>(result: T): void {}
export function assertGood<Good, Bad>(
	result: GoodBadResult<Good, Bad>
): asserts result is GoodResult<Good> {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (isBad(result)) {
		let errStr = errToLongString(result.error);
		if (errStr === '[object Object]') {
			errStr = JSON.stringify(result.error);
		}

		throw new Error(`Expected good result but got ${errStr}`);
	}
}

export function guardBad<T extends BadResult<unknown>>(result: T): void {}
export function assertBad<Good, Bad>(
	result: GoodBadResult<Good, Bad>
): asserts result is BadResult<Bad> {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (isGood(result)) {
		throw new Error('Expected bad result');
	}
}
export function assertBadAndUnwrap<Good, Bad>(result: GoodBadResult<Good, Bad>): Bad {
	assertBad(result);
	return result.error;
}

export function assertGoodAndUnwrap<T>(result: GoodBadResult<T, unknown>): T {
	assertGood(result);
	return result.value;
}

export function wrapAndNeverThrow<T>(func: () => T): GoodBadResult<T, unknown> {
	try {
		const result = func();
		return good(result);
	} catch (err) {
		return bad(err);
	}
}

export async function wrapAndNeverThrowAsync<T>(
	func: () => Promise<T>
): Promise<GoodBadResult<T, unknown>> {
	try {
		const result = await func();
		return good(result);
	} catch (err) {
		return bad(err);
	}
}

type SyncOrAsync<T> = T | Promise<T>;
export async function tryUntilGood<T, V>(
	options: {
		maxWaitTime: string | number;
		delayBetweenTries: string | number;
	},
	fn: () => SyncOrAsync<GoodBadResult<T, V>>
): Promise<
	GoodBadResult<
		T,
		{
			numTries: number;
			elapsedMs: number;
			badResult: V;
		}
	>
> {
	const delayMs = durationToMs(options.delayBetweenTries);
	const maxWaitTime = durationToMs(options.maxWaitTime);

	const start = Date.now();
	let numTries = 0;

	while (true) {
		numTries++;

		const result = await Promise.resolve(fn());
		// If result is good, return good result
		if (isGood(result)) {
			return result;
		}

		// If result is bad, return bad if we have waited too long
		const now = Date.now();
		const elapsedMs = now - start;
		if (elapsedMs + delayMs > maxWaitTime) {
			return bad({
				numTries,
				elapsedMs,
				badResult: result.error,
			});
		}

		await delay(delayMs);
	}

	throw new Error('Should not reach');
}
